/**
 * useJourney — a global, tab-scoped navigation "journey" (breadcrumb trail) built
 * from the user's ACTUAL navigation path, not a fixed hierarchy.
 *
 * How it works
 *  - The trail lives in React Router history `state` (`state.journey`), so it is
 *    scoped to the browser tab, survives refresh, and — crucially — Back/Forward
 *    restore each history entry's own frozen snapshot. On direct URL entry /
 *    bookmark, `state` is null, so the trail is empty and no ancestors are
 *    fabricated.
 *  - It integrates with the existing List-State system by value, not by
 *    re-implementing it: each entry stores the full "pathname?search" URL of an
 *    ancestor, so returning to that URL restores its useListState filters/page and
 *    useScrollRestore scroll for free.
 *
 * Page API
 *  - useJourneyNavigate(): returns `journeyNavigate(destUrl, { label })` — call it
 *    in place of navigate() when drilling into a child. `label` is the display
 *    name of the page being LEFT (the new ancestor).
 *  - useJourneyNode({ label, fallback }): every participating detail/child page
 *    calls this to (a) register its active label for the breadcrumb and (b) get a
 *    journey-aware `goBack` for its Back button.
 *  - useJourneyView(): consumed by the global <JourneyBreadcrumb/> only.
 *
 * Pure decision logic lives in lib/journey.js (unit-tested); the hooks only wire
 * it to react-router. No localStorage is used.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { buildJourney, resolveBack, trailForAncestor } from '@/lib/journey';
import { routeLabel } from '@/lib/journeyRoutes';

const JourneyContext = createContext(null);

export function JourneyProvider({ children }) {
  // The active page's node (set by useJourneyNode): { pathname, label, fallback }.
  const [current, setCurrent] = useState(null);
  // Global capture so the journey is recorded on EVERY in-app click, everywhere.
  useJourneyAutoCapture(current);
  const value = useMemo(() => ({ current, setCurrent }), [current]);
  return <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>;
}

function useJourneyContext() {
  const ctx = useContext(JourneyContext);
  if (!ctx) throw new Error('useJourney hooks must be used within <JourneyProvider>');
  return ctx;
}

const fullUrl = (location) => `${location.pathname}${location.search || ''}`;

/**
 * Global auto-capture: records the journey on EVERY in-app forward navigation
 * (plain navigate() or <Link>), so the breadcrumb appears everywhere the user
 * clicks from one page to another — not only where journeyNavigate() was used.
 * It stamps the destination history entry (replace) with a trail built from the
 * page just left, reusing the same buildJourney() as the explicit API. It NEVER
 * runs on Back/Forward (POP), on REPLACE, or on entries that already carry a
 * journey (explicit journeyNavigate / restored snapshot) — so it cannot loop and
 * never fabricates a trail on direct URL entry (no previous in-app page).
 */
function useJourneyAutoCapture(current) {
  const location = useLocation();
  const navigate = useNavigate();
  const navType = useNavigationType();
  const lastRef = useRef(null);

  useEffect(() => {
    const url = fullUrl(location);
    const path = location.pathname;
    const stateJourney = location.state?.journey;
    const label = current && current.pathname === path ? current.label : routeLabel(path);

    if (stateJourney) {
      // Explicit journeyNavigate/link, or a restored Back/Forward snapshot — trust it.
      lastRef.current = { url, path, label, journey: stateJourney };
      return;
    }
    const prev = lastRef.current;
    if (navType === 'PUSH' && prev && prev.path !== path) {
      const journey = buildJourney(
        prev.journey || [],
        { url: prev.url, label: prev.label, key: prev.path },
        url,
      );
      if (journey.length > 0) {
        navigate(url, { replace: true, state: { ...(location.state || {}), journey } });
        return; // lastRef refreshes on the REPLACE re-run (stateJourney now set)
      }
    }
    lastRef.current = { url, path, label, journey: stateJourney || [] };
  }, [location, navType, current, navigate]);
}

/**
 * Navigate into a child page, appending the CURRENT page to the journey trail.
 * @param {string} destUrl  destination ("pathname?search").
 * @param {Object} opts      { label, replace, state } — `label` names the page
 *                           being left (the new ancestor node).
 */
export function useJourneyNavigate() {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (destUrl, { label, replace = false, state = {} } = {}) => {
      const incoming = location.state?.journey || [];
      const self = { url: fullUrl(location), label, key: location.pathname };
      const journey = buildJourney(incoming, self, destUrl);
      navigate(destUrl, { replace, state: { ...state, journey } });
    },
    [navigate, location],
  );
}

/**
 * Journey-aware props for a react-router <Link>. Preserves native link semantics
 * (modified-click / middle-click open a new tab) while attaching the trail for a
 * plain SPA navigation. Spread onto a Link: `<Link {...journeyLink(url, { label })}>`.
 * @returns {(destUrl: string, opts?: { label?: string, state?: object }) => { to, state }}
 */
export function useJourneyLink() {
  const location = useLocation();
  return useCallback(
    (destUrl, { label, state = {} } = {}) => {
      const incoming = location.state?.journey || [];
      const self = { url: fullUrl(location), label, key: location.pathname };
      const journey = buildJourney(incoming, self, destUrl);
      return { to: destUrl, state: { ...state, journey } };
    },
    [location],
  );
}

/**
 * Register the current page as a journey node and expose a journey-aware Back.
 * @param {Object} opts { label, fallback } — `label` is the current page's
 *                       display name; `fallback` is the route used on direct entry.
 * @returns { goBack, journey, hasJourney }
 */
export function useJourneyNode({ label, fallback = '/' } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrent } = useJourneyContext();

  useEffect(() => {
    setCurrent({ pathname: location.pathname, label, fallback });
    return () =>
      setCurrent((c) => (c && c.pathname === location.pathname ? null : c));
  }, [location.pathname, label, fallback, setCurrent]);

  const journey = location.state?.journey || [];

  const goBack = useCallback(() => {
    const trail = location.state?.journey || [];
    const decision = resolveBack({
      journey: trail,
      hasHistory: location.key !== 'default',
      fallback,
    });
    if (decision.type === 'history') navigate(-1);
    else if (decision.type === 'url') navigate(decision.url, { state: { journey: decision.journey } });
    else navigate(decision.url);
  }, [navigate, location, fallback]);

  return { goBack, journey, hasJourney: journey.length > 0 };
}

/** State + actions for the global <JourneyBreadcrumb/>. */
export function useJourneyView() {
  const location = useLocation();
  const navigate = useNavigate();
  const { current } = useJourneyContext();
  const journey = location.state?.journey || [];

  const goToAncestor = useCallback(
    (i) => {
      const trail = location.state?.journey || [];
      const target = trail[i];
      if (!target) return;
      // Direct navigation to the saved URL, with that ancestor's own trail. This
      // pushes a fresh entry (never mutates a previous one) and restores the exact
      // saved list-state/scroll via that URL.
      navigate(target.url, { state: { journey: trailForAncestor(trail, i) } });
    },
    [location, navigate],
  );

  // Prefer the page's registered label; otherwise fall back to a readable route
  // label so the current node always has a name (guards against a stale label
  // flashing from a different page during a route transition).
  const currentLabel =
    current && current.pathname === location.pathname
      ? current.label
      : routeLabel(location.pathname);

  return { journey, currentLabel, goToAncestor };
}
